// src/components/common/ExamGrid.tsx
// ============================================
// HERBRUIKBARE EXAM GRID COMPONENT
// Modern Glassmorphism Design - Fully Responsive
// Linkt naar SEO pagina's voor betere vindbaarheid
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { examConfigs } from '../../config/examConfigs';

// ============================================
// EXAM MODULE DATA (met icons en toetstermen)
// Gecentraliseerde data voor consistentie
// ============================================

interface ExamModuleDisplay {
  id: string;
  name: string;
  icon: string;
  toetstermen: number;
  slug: string;
  isActive: boolean;
}

// Mapping van exam IDs naar display icons
// (examConfigs bevat geen icons, dus we voegen ze hier toe)
const EXAM_ICONS: Record<string, string> = {
  'basis': '📚',
  'schade-particulier': '🏠',
  'schade-zakelijk': '🏢',
  'hypotheken': '🏡',
  'pensioen': '👴',
  'vermogen': '💰',
  'inkomen': '💼',
  'consumptief-krediet': '💳',
  'zorgverzekeringen': '🏥',
};

// Toetstermen per module (officiële CDFD aantallen)
const TOETSTERMEN_COUNT: Record<string, number> = {
  'basis': 168,
  'schade-particulier': 162,
  'schade-zakelijk': 158,
  'hypotheken': 102,
  'pensioen': 145,
  'vermogen': 198,
  'inkomen': 163,
  'consumptief-krediet': 56,
  'zorgverzekeringen': 144,
};

// Volgorde voor weergave
const DISPLAY_ORDER = [
  'basis',
  'schade-particulier',
  'schade-zakelijk',
  'hypotheken',
  'pensioen',
  'vermogen',
  'inkomen',
  'consumptief-krediet',
  'zorgverzekeringen',
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Haal alle exam modules op voor weergave
 * Combineert data uit examConfigs met icons en toetstermen
 */
const getExamModulesForDisplay = (): ExamModuleDisplay[] => {
  return DISPLAY_ORDER.map(id => {
    const config = examConfigs[id];
    
    // Fallback voor examens die nog niet in examConfigs staan
    if (!config) {
      return {
        id,
        name: id.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        icon: EXAM_ICONS[id] || '📋',
        toetstermen: TOETSTERMEN_COUNT[id] || 0,
        slug: `wft-${id}`,
        isActive: false,
      };
    }

    return {
      id: config.id,
      name: config.name,
      icon: EXAM_ICONS[config.id] || '📋',
      toetstermen: TOETSTERMEN_COUNT[config.id] || 0,
      slug: config.slug,
      isActive: config.isActive,
    };
  });
};

// ============================================
// COMPONENT PROPS
// ============================================

interface ExamGridProps {
  /** Toon alleen actieve examens */
  activeOnly?: boolean;
  /** Toon de titel boven de grid */
  showTitle?: boolean;
  /** Custom titel tekst */
  title?: string;
  /** Maximaal aantal kolommen op desktop */
  maxColumns?: 3 | 4 | 5;
  /** Toon toetstermen aantal */
  showToetstermen?: boolean;
  /** Custom className voor de container */
  className?: string;
  /** Variant: 'default' voor landing page, 'compact' voor kleinere weergave */
  variant?: 'default' | 'compact';
}

// ============================================
// EXAM CARD COMPONENT
// ============================================

interface ExamCardProps {
  module: ExamModuleDisplay;
  variant: 'default' | 'compact';
  showToetstermen: boolean;
}

const ExamCard: React.FC<ExamCardProps> = ({ module, variant, showToetstermen }) => {
  const isCompact = variant === 'compact';
  
  return (
    <Link
      to={`/${module.slug}`}
      className={`
        group relative block
        bg-white/60 backdrop-blur-sm 
        rounded-xl 
        border border-white/50 
        hover:shadow-xl hover:scale-[1.02] hover:bg-white/80
        transition-all duration-300 ease-out
        ${isCompact ? 'p-3' : 'p-4'}
        cursor-pointer
        overflow-hidden
      `}
      aria-label={`Bekijk ${module.name} examen informatie`}
    >
      {/* Hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/10 transition-all duration-300 rounded-xl" />
      
      {/* Content */}
      <div className="relative text-center">
        {/* Icon */}
        <span 
          className={`
            block mb-2 
            transition-transform duration-300 
            group-hover:scale-110 group-hover:-translate-y-0.5
            ${isCompact ? 'text-xl' : 'text-2xl'}
          `}
          role="img" 
          aria-hidden="true"
        >
          {module.icon}
        </span>
        
        {/* Naam */}
        <p 
          className={`
            font-medium text-gray-700 
            group-hover:text-gray-900
            transition-colors duration-200
            ${isCompact ? 'text-xs' : 'text-sm'}
          `}
        >
          {module.name}
        </p>
        
        {/* Toetstermen */}
        {showToetstermen && module.toetstermen > 0 && (
          <p 
            className={`
              text-gray-400 mt-1
              group-hover:text-gray-500
              transition-colors duration-200
              ${isCompact ? 'text-[10px]' : 'text-xs'}
            `}
          >
            {module.toetstermen} toetstermen
          </p>
        )}
      </div>
      
      {/* Arrow indicator on hover */}
      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
        <svg 
          className={`text-blue-500 ${isCompact ? 'w-3 h-3' : 'w-4 h-4'}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 5l7 7-7 7" 
          />
        </svg>
      </div>
    </Link>
  );
};

// ============================================
// MAIN EXAM GRID COMPONENT
// ============================================

const ExamGrid: React.FC<ExamGridProps> = ({
  activeOnly = false,
  showTitle = true,
  title = '📖 Beschikbare WFT Examens',
  maxColumns = 5,
  showToetstermen = true,
  className = '',
  variant = 'default',
}) => {
  // Haal exam modules op
  let modules = getExamModulesForDisplay();
  
  // Filter op actieve examens indien gewenst
  if (activeOnly) {
    modules = modules.filter(m => m.isActive);
  }

  // Bepaal grid kolommen op basis van maxColumns prop
  const gridColsClass = {
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
    5: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5',
  }[maxColumns];

  return (
    <div className={className}>
      {/* Titel */}
      {showTitle && (
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
          {title}
        </h3>
      )}
      
      {/* Grid */}
      <div className={`grid ${gridColsClass} gap-4 max-w-4xl mx-auto`}>
        {modules.map((module) => (
          <ExamCard
            key={module.id}
            module={module}
            variant={variant}
            showToetstermen={showToetstermen}
          />
        ))}
      </div>
    </div>
  );
};

export default ExamGrid;

// ============================================
// NAMED EXPORTS voor specifieke use cases
// ============================================

export { ExamCard, getExamModulesForDisplay };
export type { ExamModuleDisplay, ExamGridProps };